import { ElementType, ReactNode } from "react";
import clsx from "clsx";
import styled from "@emotion/styled";
import { PopperPlacementType } from "@mui/material";
import { InfoCircle16Icon } from "@kaltura/ds-react-icons";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { composeClasses, Stack } from "@kaltura/mediaspace-shared-styled";
import { Tooltip, Typography, TypographyProps } from "@kaltura/ds-react-components";
import { BaseFormLabelClasses, getBaseFormLabelClass } from "./baseFormLabelClasses";

export { baseFormLabelClasses } from "./baseFormLabelClasses";

export interface BaseFormLabelProps extends Pick<TypographyProps, "variant"> {
    className?: string;
    classes?: Partial<BaseFormLabelClasses>;
    text: string;
    htmlFor?: string;
    id?: string;
    required?: boolean;
    additionalInfo?: string;
    /** replace "label" with another tag or component */
    component?: ElementType;
    /** render additional action components next to the label */
    actions?: ReactNode;
    /**
     * Description to be shown in a tooltip on hover of the info icon next to the label. Can be a string or a React node.
     */
    description?: string | React.ReactNode;
    /**
     * Placement of the tooltip relative to the info icon.
     */
    tooltipPlacement?: PopperPlacementType;
}

const StyledLabel = styled("label")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        display: "block",
        marginBottom: theme.spacing(1),
    },
}));

const StyledTypography = styled(
    Typography,
    {
        shouldForwardProp(propName) {
            return propName !== "additionalInfo";
        },
    }
)<{ additionalInfo?: boolean }>(({ theme, additionalInfo }) => ({
    color: theme.kaltura.palette.tone2,
    ...(
        additionalInfo && {
            ...theme.kaltura.typography.formLabel,
            fontSize: theme.typography.pxToRem(13),
        }
    ),
}));

const useUtilityClasses = ({ classes }: Partial<BaseFormLabelProps>) => {
    const slots = {
        root: ["root"],
        label: ["label"],
        labelInfo: ["labelInfo"],
    };

    return composeClasses(slots, getBaseFormLabelClass, classes);
};

const StyledTitleContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(0.5),
}));

const StyledSpan = styled("div")(({ theme }) => ({
    height: 16,
    width: 16,
}));

/**
 * Form Label
 */
export const BaseFormLabel = (props: BaseFormLabelProps) => {
    const {
        component,
        htmlFor,
        id,
        text,
        required,
        className,
        variant = "body2",
        additionalInfo,
        actions,
        description,
        tooltipPlacement,
    } = props;
    const classes = useUtilityClasses(props);

    return (
        <StyledLabel as={component} htmlFor={htmlFor} id={id} className={clsx(classes.root, className)}>
            <Stack direction={"row"} gap={3} alignItems={"center"} justifyContent={"space-between"}>
                <StyledTitleContainer>
                    <StyledTypography variant={variant} component={"span"} className={classes.label}>
                        {HtmlReactParser(String(text))}
                        {required ? " *" : ""}
                    </StyledTypography>
                    {description && (
                        <Tooltip title={description} placement={tooltipPlacement}>
                            <StyledSpan tabIndex={0} role={"img"} aria-label="info">
                                <InfoCircle16Icon />
                            </StyledSpan>
                        </Tooltip>
                    )}
                </StyledTitleContainer>
                {actions && (
                    <Stack direction={"row"} gap={1} alignItems={"center"}>
                        {actions}
                    </Stack>
                )}
            </Stack>

            {additionalInfo && (
                <StyledTypography component={"span"} className={classes.labelInfo} additionalInfo={true}>
                    {HtmlReactParser(String(additionalInfo))}
                </StyledTypography>
            )}
        </StyledLabel>
    );
};
